import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Divider,
  Grid,
  Header,
  Segment,
  Table,
  Message,
  Popup,
  Icon,
} from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { closeModal, openModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import {
  // addTransClientInDb,
  updateTransFieldsInDb,
  addPartyToDb,
} from "../../../app/firestore/firestoreService";
import { onSnapshot } from "firebase/firestore";
import { fetchTransActiveFromDb } from "../../../app/firestore/firestoreService";
import { dataFromSnapshot } from "../../../app/firestore/firestoreService";

export default function DualAgentLinkExistingModal({ sellerTransaction }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  // const { currentUserProfile } = useSelector((state) => state.profile);
  const { parties } = useSelector((state) => state.transaction);
  const [buyerTransactions, setBuyerTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isLinking, setIsLinking] = useState(false);
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Fetch active buyer transactions for this agent
    const unsubscribe = onSnapshot(fetchTransActiveFromDb(), (snapshot) => {
      const transactions = snapshot.docs.map((doc) => dataFromSnapshot(doc));
      // Filter for buyer transactions only
      const buyerTrans = transactions.filter(
        (trans) =>
          trans.agentRepresents === "Buyer" &&
          trans.active === true &&
          trans.status === "Active Buyer" &&
          trans.userId === sellerTransaction.userId &&
          !trans.isLinked &&
          !trans.linkedTransactionId &&
          (!trans.sharingWithRole?.listingAgent ||
            trans.sharingWithRole.listingAgent.length === 0)
      );
      if (isMountedRef.current) {
        setBuyerTransactions(buyerTrans);
        setLoading(false);
      }
    });

    return () => {
      isMountedRef.current = false;
      unsubscribe();
    };
  }, [sellerTransaction.userId]);

  function handleClose() {
    dispatch(
      closeModal({
        modalType: "DualAgentLinkExistingModal",
      })
    );
  }

  async function handleLink() {
    if (!selectedTransaction) {
      toast.error("Please select a buyer transaction");
      return;
    }

    // Initialize progress steps
    let currentSteps = [
      {
        label: "Add you as Listing Agent to Buyer Transaction",
        status: "pending",
        error: null,
      },
      {
        label: "Add you as Buyer Agent to Seller Transaction",
        status: "pending",
        error: null,
      },
      {
        label: "Copy Title Company into Buyer Transaction",
        status: "pending",
        error: null,
      },
      {
        label: "Copy Seller Clients into Buyer Transaction",
        status: "pending",
        error: null,
      },
      {
        label: "Copy Buyer Clients into Seller Transaction",
        status: "pending",
        error: null,
      },
      {
        label: "Link Buyer Transaction to Seller Transaction",
        status: "pending",
        error: null,
      },
      {
        label: "Link Seller Transaction to Buyer Transaction",
        status: "pending",
        error: null,
      },
    ];

    // Close current modal and open progress modal
    dispatch(closeModal({ modalType: "DualAgentLinkExistingModal" }));
    dispatch(
      openModal({
        modalType: "DualAgentProgressModal",
        modalProps: { steps: currentSteps, isComplete: false },
      })
    );

    const updateStep = (index, status, error = null) => {
      currentSteps = currentSteps.map((step, i) =>
        i === index ? { ...step, status, error } : step
      );
      dispatch(
        openModal({
          modalType: "DualAgentProgressModal",
          modalProps: { steps: currentSteps, isComplete: false },
        })
      );
    };

    const completeProgress = () => {
      dispatch(
        openModal({
          modalType: "DualAgentProgressModal",
          modalProps: { steps: currentSteps, isComplete: true },
        })
      );
    };

    setIsLinking(true);
    try {
      // Step 1: Add Listing Agent (from seller transaction) to buyer transaction
      updateStep(0, "loading");
      const listingAgentParty = {
        firstName: sellerTransaction.agentProfile?.firstName || "",
        lastName: sellerTransaction.agentProfile?.lastName || "",
        email: sellerTransaction.agentProfile?.email || "",
        phone: sellerTransaction.agentProfile?.phone || "",
        companyName: sellerTransaction.agentProfile?.brokerageName || "",
        brokerLicenseNumber:
          sellerTransaction.agentProfile?.brokerLicenseNumber || "",
        brokerageLicenseNumber:
          sellerTransaction.agentProfile?.brokerageLicenseNumber || "",
        brokerageName: sellerTransaction.agentProfile?.brokerageName || "",
        brokerageId: sellerTransaction.agentProfile?.brokerageId || "",
        linkedUserId: sellerTransaction.userId || "",
        managerId: sellerTransaction.managerId || "",
        hasCoAgent: false,
        hasTransactionCoordinator: false,
        isLinked: true,
        isUser: true,
        linkedTransactionId: sellerTransaction.id,
        transactionId: selectedTransaction.id,
        role: "Listing Agent",
        type: "Party",
      };
      console.log("listingAgentParty:", listingAgentParty);
      await addPartyToDb(
        listingAgentParty,
        {
          id: selectedTransaction.id,
          userId: sellerTransaction.userId,
          managerId: sellerTransaction.managerId || "",
        },
        []
      );
      updateStep(0, "success");

      // Step 2: Add Buyer Agent to Listing transaction
      updateStep(1, "loading");
      const buyerAgentParty = {
        ...listingAgentParty,
        role: "Buyer Agent",
        type: "Party",
        transactionId: sellerTransaction.id,
        linkedTransactionId: selectedTransaction.id,
      };
      console.log("buyerAgentParty:", buyerAgentParty);
      await addPartyToDb(
        buyerAgentParty,
        {
          id: sellerTransaction.id,
          userId: sellerTransaction.userId,
          managerId: sellerTransaction.managerId || "",
        },
        []
      );
      updateStep(1, "success");

      // Step 3: Copy Title Company into Buyer Transaction (we'll do this later)
      // Step 4: Copy Seller Clients into Buyer Transaction (we'll do this next)
      // Step 5: Copy Buyer client names into Seller transaction parties
      updateStep(4, "loading");
      if (selectedTransaction.client) {
        const buyerClientParty = {
          firstName: selectedTransaction.client.firstName || "",
          middleName: selectedTransaction.client.middleName || "",
          lastName: selectedTransaction.client.lastName || "",
          email: selectedTransaction.client.email || "",
          phone: selectedTransaction.client.phone || "",
          isTrust: selectedTransaction.client.isTrust || false,
          entityName: selectedTransaction.client.entityName || "",
          signerName: selectedTransaction.client.signerName || "",
          role: "Buyer",
          type: "Party",
        };
        await addPartyToDb(
          buyerClientParty,
          {
            id: sellerTransaction.id,
            userId: sellerTransaction.userId,
            managerId: sellerTransaction.managerId || "",
          },
          []
        );
        // await addTransClientInDb(sellerTransaction, buyerClientParty);
      }

      if (
        selectedTransaction.clientSecondaryExists &&
        selectedTransaction.clientSecondary?.lastName
      ) {
        const buyerClient2Party = {
          firstName: selectedTransaction.clientSecondary.firstName || "",
          middleName: selectedTransaction.clientSecondary.middleName || "",
          lastName: selectedTransaction.clientSecondary.lastName || "",
          email: selectedTransaction.clientSecondary.email || "",
          phone: selectedTransaction.clientSecondary.phone || "",
          isTrust: selectedTransaction.clientSecondary.isTrust || false,
          entityName: selectedTransaction.clientSecondary.entityName || "",
          role: "Buyer 2",
          type: "Party",
        };
        await addPartyToDb(
          buyerClient2Party,
          {
            id: sellerTransaction.id,
            userId: sellerTransaction.userId,
            managerId: sellerTransaction.managerId || "",
          },
          []
        );
        // await addTransClientInDb(sellerTransaction, buyerClient2Party);
      }
      updateStep(4, "success");

      // Step 4: Copy Seller client names into Buyer transaction
      updateStep(3, "loading");
      if (sellerTransaction.client) {
        const sellerClientParty = {
          ...sellerTransaction.client,
          // firstName: sellerTransaction.client.firstName || "",
          // middleName: sellerTransaction.client.middleName || "",
          // lastName: sellerTransaction.client.lastName || "",
          // email: sellerTransaction.client.email || "",
          // phone: sellerTransaction.client.phone || "",
          // isTrust: sellerTransaction.client.isTrust || false,
          // entityName: sellerTransaction.client.entityName || "",
          role: "Seller",
          type: "Party",
        };
        await addPartyToDb(
          sellerClientParty,
          {
            id: selectedTransaction.id,
            userId: sellerTransaction.userId,
            managerId: sellerTransaction.managerId || "",
          },
          []
        );
        // await addTransClientInDb(selectedTransaction, sellerClientParty);
      }

      if (
        sellerTransaction.clientSecondaryExists &&
        sellerTransaction.clientSecondary?.lastName
      ) {
        const sellerClient2Party = {
          ...sellerTransaction.clientSecondary,
          // firstName: sellerTransaction.clientSecondary.firstName || "",
          // middleName: sellerTransaction.clientSecondary.middleName || "",
          // lastName: sellerTransaction.clientSecondary.lastName || "",
          // email: sellerTransaction.clientSecondary.email || "",
          // phone: sellerTransaction.clientSecondary.phone || "",
          // isTrust: sellerTransaction.clientSecondary.isTrust || false,
          // entityName: sellerTransaction.clientSecondary.entityName || "",
          role: "Seller 2",
          type: "Party",
        };
        await addPartyToDb(
          sellerClient2Party,
          {
            id: selectedTransaction.id,
            userId: sellerTransaction.userId,
            managerId: sellerTransaction.managerId || "",
          },
          []
        );
        // await addTransClientInDb(selectedTransaction, sellerClient2Party);
      }
      updateStep(3, "success");

      // Step 3: Copy Title Company party into Buyer transaction if it exists
      updateStep(2, "loading");
      const titleCompanyParty = parties?.find(
        (p) => p.role === "Title Company"
      );
      if (titleCompanyParty) {
        const titlePartyForBuyer = {
          ...titleCompanyParty,
          id: undefined, // Remove old id so new one is generated
        };
        delete titlePartyForBuyer.id;
        await addPartyToDb(
          titlePartyForBuyer,
          {
            id: selectedTransaction.id,
            userId: sellerTransaction.userId,
            managerId: sellerTransaction.managerId || "",
          },
          []
        );
      }
      updateStep(2, "success");

      // Step 6: Link Buyer Transaction to Seller Transaction
      updateStep(5, "loading");
      await updateTransFieldsInDb(selectedTransaction.id, {
        linkedTransactionId: sellerTransaction.id,
      });
      updateStep(5, "success");

      // Step 7: Link Seller Transaction to Buyer Transaction
      updateStep(6, "loading");
      await updateTransFieldsInDb(sellerTransaction.id, {
        linkedTransactionId: selectedTransaction.id,
      });
      updateStep(6, "success");

      toast.success("Transactions linked successfully");
      completeProgress();
      if (isMountedRef.current) {
        setIsLinking(false);
      }
    } catch (error) {
      toast.error(error.message);
      // Mark current step as error
      const currentStepIndex = currentSteps.findIndex(
        (s) => s.status === "loading"
      );
      if (currentStepIndex !== -1) {
        updateStep(currentStepIndex, "error", error.message);
      }
      completeProgress();
      if (isMountedRef.current) {
        setIsLinking(false);
      }
    }
  }

  return (
    <ModalWrapper size="large">
      <Segment>
        <Grid>
          <Grid.Column width={16} className="zero bottom padding">
            <Header as="h2" color="blue" className="zero bottom margin">
              Link to Existing Buyer Transaction
            </Header>
            <Divider className="zero bottom margin" />
          </Grid.Column>
          <Grid.Column width={16}>
            <p className="text-medium">
              Select an active buyer transaction to link with this seller
              transaction.
              <Popup
                content="Only Active Buyer transactions that do not already have a
                Listing Agent added to Parties are shown below.
                If your Buyer transaction has a Listing Agent and/or Sellers
                added to Parties, go to that transaction and clean up the
                transaction and remove the Listing Agent and Sellers then try
                again here."
                className="background-orange"
                trigger={<Icon name="info circle" color="blue" />}
              />
            </p>

            {loading && <Message>Loading buyer transactions...</Message>}

            {!loading && buyerTransactions.length === 0 && (
              <Message warning>
                <Message.Header>
                  No Active Buyer Transactions Found
                </Message.Header>
                <p>You don't have any active buyer transactions to link.</p>
              </Message>
            )}

            {!loading && buyerTransactions.length > 0 && (
              <Table selectable celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Select</Table.HeaderCell>
                    <Table.HeaderCell>Transaction Name</Table.HeaderCell>
                    <Table.HeaderCell>Client Name</Table.HeaderCell>
                    <Table.HeaderCell>Address</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {buyerTransactions.map((trans) => (
                    <Table.Row
                      key={trans.id}
                      active={selectedTransaction?.id === trans.id}
                      onClick={() => setSelectedTransaction(trans)}
                      style={{ cursor: "pointer" }}
                    >
                      <Table.Cell>
                        <input
                          type="radio"
                          checked={selectedTransaction?.id === trans.id}
                          onChange={() => setSelectedTransaction(trans)}
                        />
                      </Table.Cell>
                      <Table.Cell>{trans.title || "Untitled"}</Table.Cell>
                      <Table.Cell>
                        {trans.client?.firstName} {trans.client?.lastName}
                      </Table.Cell>
                      <Table.Cell>
                        {trans.address?.street || "No address"}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}

            <Divider className="medium top margin" />
            <Button
              loading={isLinking}
              disabled={!selectedTransaction || isLinking}
              type="button"
              floated={isMobile ? null : "right"}
              color="blue"
              onClick={handleLink}
              content="Link"
              className={isMobile ? "fluid medium bottom margin" : null}
            />
            <Button
              disabled={isLinking}
              onClick={handleClose}
              type="button"
              floated={isMobile ? null : "right"}
              content="Cancel"
              className={isMobile ? "fluid" : null}
            />
          </Grid.Column>
        </Grid>
      </Segment>
    </ModalWrapper>
  );
}

